import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Clock, Activity, Printer, DollarSign, ChevronLeft, ChevronRight, Edit, Filter, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDateStr(str) {
  if (!str) return '-';
  try {
    const d = new Date(str + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return str; }
}

function formatDayName(str) {
  if (!str) return '-';
  try {
    return new Date(str + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
  } catch { return str; }
}

function formatTime(isoStr) {
  if (!isoStr) return '-';
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return '-'; }
}

function formatMinutes(m) {
  if (!m && m !== 0) return '-';
  const h = Math.floor(m / 60);
  const mins = m % 60;
  return h > 0 ? `${h}:${String(mins).padStart(2, '0')}` : `0:${String(mins).padStart(2, '0')}`;
}

export default function StaffSupervisionPage() {
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [staffProfile, setStaffProfile] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const m = getMonday(new Date());
    return m.toISOString().split('T')[0];
  });
  const [weeklyData, setWeeklyData] = useState(null);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payroll');

  // Hourly rate editing
  const [editingRate, setEditingRate] = useState(false);
  const [rateValue, setRateValue] = useState('');
  const [rateSaving, setRateSaving] = useState(false);

  // Time correction
  const [correctionDialog, setCorrectionDialog] = useState(null);
  const [correctionData, setCorrectionData] = useState({});
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSaving, setCorrectionSaving] = useState(false);

  // Activity log filters
  const [activityStartDate, setActivityStartDate] = useState('');
  const [activityEndDate, setActivityEndDate] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  // Live status
  const [liveStatus, setLiveStatus] = useState(null);

  const printRef = useRef(null);

  // Fetch staff members on mount
  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/staff-members`);
        setStaffMembers(res.data);
        // Auto-select first office_admin
        const officeAdmin = res.data.find(s => s.role === 'office_admin');
        if (officeAdmin) setSelectedStaff(officeAdmin.id);
        else if (res.data.length > 0) setSelectedStaff(res.data[0].id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  // Fetch data when staff or week changes
  const fetchPayrollData = useCallback(async () => {
    if (!selectedStaff) return;
    try {
      const [weeklyRes, historyRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/api/payroll/weekly`, { params: { user_id: selectedStaff, week_start: weekStart } }),
        axios.get(`${API_URL}/api/payroll/history`, { params: { user_id: selectedStaff } }),
        axios.get(`${API_URL}/api/staff/${selectedStaff}/profile`)
      ]);
      setWeeklyData(weeklyRes.data);
      setPayrollHistory(historyRes.data);
      setStaffProfile(profileRes.data);
      setRateValue(profileRes.data.hourly_rate?.toString() || '');
    } catch (e) { console.error(e); }
  }, [selectedStaff, weekStart]);

  useEffect(() => { fetchPayrollData(); }, [fetchPayrollData]);

  // Poll live status every 10 seconds
  useEffect(() => {
    if (!selectedStaff) return;
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/time-clock/live-status/${selectedStaff}`);
        setLiveStatus(res.data);
      } catch (e) {}
    };
    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => clearInterval(interval);
  }, [selectedStaff]);

  // Fetch activity log
  const fetchActivityLog = useCallback(async () => {
    if (!selectedStaff) return;
    try {
      const res = await axios.get(`${API_URL}/api/activity-log`, {
        params: {
          user_id: selectedStaff,
          ...(activityStartDate && { start_date: activityStartDate }),
          ...(activityEndDate && { end_date: activityEndDate }),
          ...(entityFilter !== 'all' && { entity_type: entityFilter }),
          limit: 200
        }
      });
      setActivityLog(res.data);
    } catch (e) { console.error(e); }
  }, [selectedStaff, activityStartDate, activityEndDate, entityFilter]);

  useEffect(() => { if (activeTab === 'activity') fetchActivityLog(); }, [fetchActivityLog, activeTab]);

  const navigateWeek = (dir) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + (dir * 7));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const handleSaveRate = async () => {
    setRateSaving(true);
    try {
      await axios.put(`${API_URL}/api/staff/${selectedStaff}/hourly-rate`, { hourly_rate: parseFloat(rateValue) || 0 });
      setEditingRate(false);
      toast.success('Hourly rate updated');
      fetchPayrollData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update rate'); }
    finally { setRateSaving(false); }
  };

  const handleCorrection = async () => {
    setCorrectionSaving(true);
    try {
      await axios.put(`${API_URL}/api/time-clock/${correctionDialog.id}/correct`, {
        ...correctionData,
        reason: correctionReason
      });
      setCorrectionDialog(null);
      setCorrectionData({});
      setCorrectionReason('');
      toast.success('Time entry corrected');
      fetchPayrollData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save correction'); }
    finally { setCorrectionSaving(false); }
  };

  const handlePrint = () => window.print();

  const handleExportPDF = () => {
    // Use print-to-PDF via the browser's print dialog
    window.print();
  };

  const liveStatusIcon = liveStatus?.status === 'working' ? '🟢' : liveStatus?.status === 'on_lunch' ? '🟡' : liveStatus?.status === 'clocked_out' ? '🔴' : '⚪';
  const liveStatusLabel = liveStatus?.status === 'working' ? 'WORKING' : liveStatus?.status === 'on_lunch' ? 'ON LUNCH' : liveStatus?.status === 'clocked_out' ? 'CLOCKED OUT' : 'NOT STARTED';

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
  };

  const roleLabels = { aline: 'Full Admin', administrative: 'Administrative', operator: 'Operator', office_admin: 'Office Admin' };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div></DashboardLayout>;
  }

  const weekEndDate = (() => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + 4);
    return d.toISOString().split('T')[0];
  })();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-supervision-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Staff & Payroll</h1>
            <p className="text-sm text-slate-500 mt-1">Employee time tracking, payroll reports and audit trail</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1" /> Export PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-btn">
              <Printer className="w-4 h-4 mr-1" /> Print Report
            </Button>
          </div>
        </div>

        {/* LIVE STATUS — Real-time employee status */}
        {liveStatus && selectedStaff && (
          <Card className={`shadow-sm border-l-4 ${liveStatus.status === 'working' ? 'border-l-emerald-500 bg-emerald-50' : liveStatus.status === 'on_lunch' ? 'border-l-amber-500 bg-amber-50' : liveStatus.status === 'clocked_out' ? 'border-l-rose-500 bg-rose-50' : 'border-l-slate-300 bg-slate-50'}`} data-testid="live-status-card">
            <CardContent className="py-3 px-5">
              <div className="flex items-center gap-3">
                <span className="text-lg">{liveStatusIcon}</span>
                <span className="font-semibold text-slate-900">{liveStatus.name || staffProfile?.name}</span>
                <span className="text-sm font-medium">{liveStatusLabel}</span>
                {liveStatus.clock_in && <span className="text-xs text-slate-500">— Clock In: {formatTime(liveStatus.clock_in)}</span>}
                {liveStatus.lunch_out && !liveStatus.lunch_in && <span className="text-xs text-amber-700">— Lunch since {formatTime(liveStatus.lunch_out)}</span>}
                {liveStatus.clock_out && <span className="text-xs text-slate-500">— Clock Out: {formatTime(liveStatus.clock_out)} — {liveStatus.total_hours}h paid</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff Selector */}
        <Card className="bg-white border-slate-200 shadow-sm print:hidden">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-[220px]">
                <Label className="text-xs text-slate-500 mb-1 block">Employee</Label>
                <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                  <SelectTrigger data-testid="staff-filter"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {staffMembers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} <span className="text-slate-400 text-xs ml-1">({roleLabels[s.role] || s.role})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {staffProfile && (
                <div className="flex items-center gap-4 text-sm">
                  <Badge className={staffProfile.role === 'office_admin' ? 'bg-teal-100 text-teal-800' : 'bg-rose-100 text-rose-800'}>
                    {roleLabels[staffProfile.role] || staffProfile.role}
                  </Badge>
                  <span className="text-slate-500">{staffProfile.email}</span>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                    {editingRate ? (
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.01" className="w-20 h-7 text-sm" value={rateValue} onChange={e => setRateValue(e.target.value)} data-testid="hourly-rate-input" />
                        <span className="text-xs text-slate-400">/hr</span>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={handleSaveRate} disabled={rateSaving}>{rateSaving ? '...' : 'Save'}</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setEditingRate(false)}>Cancel</Button>
                      </div>
                    ) : (
                      <button className="flex items-center gap-1 text-sm hover:underline" onClick={() => setEditingRate(true)} data-testid="edit-rate-btn">
                        <span className="font-medium text-slate-900">{staffProfile.hourly_rate != null ? `$${staffProfile.hourly_rate.toFixed(2)}/hr` : 'Not set'}</span>
                        <Edit className="w-3 h-3 text-slate-400" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="print:hidden">
            <TabsTrigger value="payroll" data-testid="tab-payroll"><DollarSign className="w-4 h-4 mr-1" /> Weekly Payroll</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><Clock className="w-4 h-4 mr-1" /> Payroll History</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity"><Activity className="w-4 h-4 mr-1" /> Activity Log</TabsTrigger>
          </TabsList>

          {/* Weekly Payroll Tab */}
          <TabsContent value="payroll">
            <div ref={printRef}>
              {/* Print Header - only visible in print */}
              <div className="hidden print:block mb-6 text-center">
                <h2 className="text-xl font-bold">Supreme Credit Inc.</h2>
                <p className="text-sm text-slate-600 mt-1">Employee Payroll Report</p>
                <p className="text-sm font-medium mt-2">Employee: {weeklyData?.employee_name}</p>
                <p className="text-sm">Pay Period: {formatDateStr(weekStart)} through {formatDateStr(weekEndDate)}</p>
                <p className="text-sm">Hourly Rate: ${weeklyData?.hourly_rate?.toFixed(2) || '0.00'}/hour</p>
              </div>

              <Card className="bg-white border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-3 print:pb-2">
                  <div>
                    <CardTitle className="text-lg print:hidden">Weekly Report — {weeklyData?.employee_name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {formatDateStr(weekStart)} — {formatDateStr(weekEndDate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 print:hidden">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(-1)} data-testid="prev-week">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateWeek(1)} data-testid="next-week">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!weeklyData?.records?.length ? (
                    <p className="text-center text-slate-500 py-8">No time records for this period</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase">Clock In</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase">Lunch Out</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase">Lunch Return</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase">Clock Out</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Lunch Time</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Paid Hours</TableHead>
                            <TableHead className="text-xs font-medium text-slate-500 uppercase print:hidden w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {weeklyData.records.map((r) => (
                            <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`payroll-row-${r.date}`}>
                              <TableCell className="font-medium text-slate-900">{formatDayName(r.date)}<br /><span className="text-xs text-slate-400">{r.date}</span></TableCell>
                              <TableCell className="text-emerald-700">{formatTime(r.clock_in)}</TableCell>
                              <TableCell className="text-amber-600">{formatTime(r.lunch_out)}</TableCell>
                              <TableCell className="text-blue-600">{formatTime(r.lunch_in)}</TableCell>
                              <TableCell className="text-rose-600">
                                {r.clock_out ? formatTime(r.clock_out) : (r.warning === 'missing_clock_out' ? <span className="text-rose-500 font-medium text-xs">⚠️ Missing</span> : '-')}
                              </TableCell>
                              <TableCell className="text-right text-slate-600">{formatMinutes(r.lunch_minutes)}</TableCell>
                              <TableCell className="text-right font-semibold text-slate-900">{r.total_hours != null ? r.total_hours.toFixed(2) : '-'}</TableCell>
                              <TableCell className="print:hidden">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setCorrectionDialog(r); setCorrectionData({ clock_in: r.clock_in, lunch_out: r.lunch_out || '', lunch_in: r.lunch_in || '', clock_out: r.clock_out || '' }); }} data-testid={`correct-btn-${r.date}`}>
                                  <Edit className="w-3.5 h-3.5 text-slate-400" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {/* Weekly Summary */}
                  {weeklyData?.records?.length > 0 && (
                    <div className="mt-6 border-t border-slate-200 pt-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="space-y-1 text-sm">
                          <p className="text-slate-500">Total Lunch: <span className="font-medium text-slate-800">{formatMinutes(weeklyData.total_lunch_minutes)}</span></p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 min-w-[280px]">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Total Paid Hours:</span>
                            <span className="font-bold text-slate-900">{weeklyData.total_hours.toFixed(2)} hours</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Hourly Rate:</span>
                            <span className="font-medium text-slate-700">${weeklyData.hourly_rate?.toFixed(2) || '0.00'}/hour</span>
                          </div>
                          <div className="border-t border-slate-300 mt-2 pt-2 flex justify-between text-base">
                            <span className="font-semibold text-slate-700">Weekly Gross Pay:</span>
                            <span className="font-bold text-emerald-700 text-lg" data-testid="weekly-gross-pay">${weeklyData.gross_pay?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payroll History Tab */}
          <TabsContent value="history">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Payroll History — {staffProfile?.name}</CardTitle>
              </CardHeader>
              <CardContent>
                {payrollHistory.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No payroll records found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Pay Period</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Days</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Total Hours</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Rate</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase text-right">Gross Pay</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payrollHistory.map((w) => (
                          <TableRow key={w.week_start} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setWeekStart(w.week_start); setActiveTab('payroll'); }} data-testid={`history-row-${w.week_start}`}>
                            <TableCell className="font-medium text-slate-900">
                              {formatDateStr(w.week_start)} — {formatDateStr(w.week_end)}
                            </TableCell>
                            <TableCell className="text-right text-slate-600">{w.days_worked}</TableCell>
                            <TableCell className="text-right font-medium text-slate-900">{w.total_hours.toFixed(2)}h</TableCell>
                            <TableCell className="text-right text-slate-600">${w.hourly_rate?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell className="text-right font-bold text-emerald-700">${w.gross_pay?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="text-xs"><Eye className="w-3 h-3 mr-1" /> View</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <CardTitle className="text-lg">Activity Log — {staffProfile?.name}</CardTitle>
                  <div className="flex flex-wrap gap-2 items-end">
                    <Input type="date" className="w-36 h-8 text-xs" value={activityStartDate} onChange={e => setActivityStartDate(e.target.value)} placeholder="Start" />
                    <Input type="date" className="w-36 h-8 text-xs" value={activityEndDate} onChange={e => setActivityEndDate(e.target.value)} placeholder="End" />
                    <Select value={entityFilter} onValueChange={setEntityFilter}>
                      <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="client">Clients</SelectItem>
                        <SelectItem value="letter">Letters</SelectItem>
                        <SelectItem value="credit_score">Scores</SelectItem>
                        <SelectItem value="payment">Payments</SelectItem>
                        <SelectItem value="note">Notes</SelectItem>
                        <SelectItem value="time_clock">Time Clock</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setActivityStartDate(''); setActivityEndDate(''); setEntityFilter('all'); }}>
                      <Filter className="w-3 h-3 mr-1" /> Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {activityLog.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No activity records found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Date/Time</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Action</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Type</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Client</TableHead>
                          <TableHead className="text-xs font-medium text-slate-500 uppercase">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityLog.map((log) => (
                          <TableRow key={log.id} className="hover:bg-slate-50">
                            <TableCell className="text-slate-700 text-sm whitespace-nowrap">{formatDateTime(log.timestamp)}</TableCell>
                            <TableCell>
                              <Badge className={
                                log.action === 'created' ? 'bg-emerald-100 text-emerald-800' :
                                log.action === 'updated' || log.action === 'corrected' ? 'bg-blue-100 text-blue-800' :
                                log.action === 'deleted' ? 'bg-rose-100 text-rose-800' :
                                'bg-slate-100 text-slate-800'
                              }>{log.action}</Badge>
                            </TableCell>
                            <TableCell className="text-slate-600 capitalize text-sm">{log.entity_type?.replace('_', ' ') || '-'}</TableCell>
                            <TableCell className="text-slate-700 text-sm">{log.client_name || '-'}</TableCell>
                            <TableCell className="text-slate-500 text-sm max-w-xs truncate">{log.details || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Time Correction Dialog */}
        <Dialog open={!!correctionDialog} onOpenChange={(open) => { if (!open) setCorrectionDialog(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Correct Time Entry — {correctionDialog?.date}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-slate-500">Original values are preserved in the audit trail. Any changes will be logged.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Clock In</Label>
                  <Input type="datetime-local" className="text-sm" value={correctionData.clock_in?.slice(0, 16) || ''} onChange={e => setCorrectionData({...correctionData, clock_in: e.target.value ? new Date(e.target.value).toISOString() : ''})} data-testid="correct-clock-in" />
                </div>
                <div>
                  <Label className="text-xs">Clock Out</Label>
                  <Input type="datetime-local" className="text-sm" value={correctionData.clock_out?.slice(0, 16) || ''} onChange={e => setCorrectionData({...correctionData, clock_out: e.target.value ? new Date(e.target.value).toISOString() : ''})} data-testid="correct-clock-out" />
                </div>
                <div>
                  <Label className="text-xs">Lunch Out</Label>
                  <Input type="datetime-local" className="text-sm" value={correctionData.lunch_out?.slice(0, 16) || ''} onChange={e => setCorrectionData({...correctionData, lunch_out: e.target.value ? new Date(e.target.value).toISOString() : ''})} data-testid="correct-lunch-out" />
                </div>
                <div>
                  <Label className="text-xs">Lunch Return</Label>
                  <Input type="datetime-local" className="text-sm" value={correctionData.lunch_in?.slice(0, 16) || ''} onChange={e => setCorrectionData({...correctionData, lunch_in: e.target.value ? new Date(e.target.value).toISOString() : ''})} data-testid="correct-lunch-in" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason for Adjustment</Label>
                <Textarea value={correctionReason} onChange={e => setCorrectionReason(e.target.value)} placeholder="Required - explain why this correction is being made" className="text-sm" data-testid="correction-reason" />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
              <Button onClick={handleCorrection} disabled={correctionSaving || !correctionReason} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-correction-btn">
                {correctionSaving ? '...' : 'Save Correction'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
